import React, { useCallback, useEffect, useRef, useState } from 'react'
import { collection, doc, getDoc, getDocs, addDoc, orderBy, query as fsQuery, where, setDoc, increment, serverTimestamp } from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import { useAuth } from '../../contexts/AuthContext'
import { BIBLE_BOOKS, OT_BOOKS, NT_BOOKS, UA_REF_MAP, EN_REF_MAP } from './bibleBooks'
import '../../assets/css/pages.css'

const BIBLE_API = 'https://bible-api.com'
const BOLLS_API = 'https://bolls.life'

// ── Fetch helpers ─────────────────────────────────────────────────────────────

async function fetchVerses(b, ch, langCode) {
  if (langCode === 'ua') {
    const res = await fetch(`${BOLLS_API}/get-chapter/UBIO/${b.bookNum}/${ch}/`)
    if (!res.ok) throw new Error()
    return (await res.json()).map(v => ({ verse: v.verse, text: v.text }))
  } else {
    const res = await fetch(`${BIBLE_API}/${b.apiName}+${ch}?translation=web`)
    if (!res.ok) throw new Error()
    return (await res.json()).verses || []
  }
}

// ── Archive matching ──────────────────────────────────────────────────────────

function chaptersInRestInclude(rest, targetChapter) {
  const groups = rest.split(',')
  for (const group of groups) {
    const m = group.trim().match(/^(\d+)/)
    if (m && Number(m[1]) === targetChapter) return true
  }
  return false
}

function singleRefMatchesChapter(ref, targetBookId, targetChapter) {
  if (!ref) return false
  const norm = ref.toLowerCase().replace(/\s+/g, '')

  for (const [abbr, bookId] of Object.entries(UA_REF_MAP)) {
    if (bookId !== targetBookId) continue
    if (!norm.startsWith(abbr)) continue
    const rest = norm.slice(abbr.length).replace(/^[.\s]+/, '')
    return chaptersInRestInclude(rest, targetChapter)
  }

  for (const book of BIBLE_BOOKS) {
    if (book.id !== targetBookId) continue
    for (const nameNorm of [book.ua.toLowerCase().replace(/\s+/g, ''), book.en.toLowerCase().replace(/\s+/g, '')]) {
      if (norm.startsWith(nameNorm)) {
        const rest = norm.slice(nameNorm.length).replace(/^[.\s]+/, '')
        return chaptersInRestInclude(rest, targetChapter)
      }
    }
  }
  return false
}

function archiveMatchesChapter(bibleField, targetBookId, targetChapter) {
  if (!bibleField || !targetBookId || !targetChapter) return false
  return bibleField.split(';').some(ref => singleRefMatchesChapter(ref.trim(), targetBookId, targetChapter))
}

// ── Reference parser ──────────────────────────────────────────────────────────

function parseReference(raw) {
  const s = raw.trim().replace(/\s*:\s*/g, ':')

  const candidates = [
    s.match(/^(.+?)\s+(\d+)(?::(\d+)(?:-(\d+))?)?\.?$/i),
    s.match(/^(.+?)\s+(\d+)\s+(\d+)$/i),
  ]

  function findBook(raw) {
    const br = raw.replace(/\.$/, '').toLowerCase().trim()
    for (const [abbr, id] of Object.entries(UA_REF_MAP)) {
      if (br === abbr) return BIBLE_BOOKS.find(b => b.id === id)
    }
    return BIBLE_BOOKS.find(b =>
      b.en.toLowerCase().startsWith(br) ||
      b.ua.toLowerCase().startsWith(br) ||
      br === b.id
    ) || null
  }

  for (const m of candidates) {
    if (!m) continue
    const book = findBook(m[1])
    if (!book) continue
    const chapter = m[2]
    const verseStart = m[3] || null
    const verseEnd = m[4] || null
    let ref = `${book.apiName}+${chapter}`
    if (verseStart) ref += `:${verseStart}`
    if (verseEnd) ref += `-${verseEnd}`
    return { book, chapter: Number(chapter), verseStart, ref }
  }
  return null
}

// ── Bookmark helpers ──────────────────────────────────────────────────────────

function loadBookmarks() {
  try { return JSON.parse(localStorage.getItem('bibleBookmarks') || '[]') } catch { return [] }
}

function saveBookmarks(list) {
  localStorage.setItem('bibleBookmarks', JSON.stringify(list))
}

function formatRelativeDate(iso, lang) {
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return lang === 'en' ? 'today' : 'сьогодні'
  if (days === 1) return lang === 'en' ? 'yesterday' : 'вчора'
  if (days < 7) return lang === 'en' ? `${days}d ago` : `${days} дн. тому`
  return new Date(iso).toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-GB', { day: 'numeric', month: 'short' })
}

// ── Sub-components ────────────────────────────────────────────────────────────

const BookGrid = ({ books, label, lang, onBook }) => (
  <div>
    <div className='bv-testament-label'>{label}</div>
    <div className='bv-book-grid'>
      {books.map(b => (
        <button key={b.id} className='bv-book-btn' onClick={() => onBook(b)}>
          {lang === 'en' ? b.en : b.ua}
        </button>
      ))}
    </div>
  </div>
)

const ChapterNav = ({ book, chapter, lang, onChapter, onBack }) => (
  <div className='bv-chapter-nav'>
    <button className='bv-back-btn' onClick={onBack}>← {lang === 'en' ? 'Books' : 'Книги'}</button>
    <span className='bv-breadcrumb'>{lang === 'en' ? book.en : book.ua}</span>
    <div className='bv-chapter-pills'>
      {Array.from({ length: book.chapters }, (_, i) => i + 1).map(n => (
        <button
          key={n}
          className={`bv-chapter-pill${n === chapter ? ' bv-chapter-pill--active' : ''}`}
          onClick={() => onChapter(n)}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
)

// ── Main component ────────────────────────────────────────────────────────────

const BibleViewer = () => {
  const { currentUser, userProfile } = useAuth()
  const isAdmin = !!currentUser?.email && (
    userProfile?.permissions?.includes('admin:super') ||
    userProfile?.permissions?.includes('admin:pages') ||
    userProfile?.permissions?.includes('admin:quiz')
  )

  const [lang] = useState(localStorage.getItem('userLanguage') === 'en' ? 'en' : 'ua')

  const [view, setView] = useState('toc')
  const [prevView, setPrevView] = useState('toc')
  const [book, setBook] = useState(null)
  const [chapter, setChapter] = useState(1)
  const [verses, setVerses] = useState([])
  const [versesAlt, setVersesAlt] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResult, setSearchResult] = useState(null)
  const [highlight, setHighlight] = useState('')
  const [selectedVerse, setSelectedVerse] = useState(null)

  // Reader settings
  const [parallel, setParallel] = useState(() => localStorage.getItem('bibleParallel') === '1')
  const [fontSize, setFontSize] = useState(() => Number(localStorage.getItem('bibleFontSize')) || 16)
  const [copied, setCopied] = useState(null)

  // Reader count
  const [readerCount, setReaderCount] = useState(null)

  // Bookmarks
  const [bookmarks, setBookmarks] = useState(loadBookmarks)
  const [dayVerseStatus, setDayVerseStatus] = useState({})

  // Archive
  const [archiveItems, setArchiveItems] = useState([])
  const [relatedSermons, setRelatedSermons] = useState([])

  const parallelRef = useRef(parallel)
  useEffect(() => { parallelRef.current = parallel }, [parallel])

  // Reader count
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10)
    const key = `bible_read_${today}`
    const ref = doc(db, 'readerStats', today)
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1')
      setDoc(ref, { count: increment(1) }, { merge: true }).catch(() => {})
    }
    getDoc(ref).then(snap => {
      setReaderCount(snap.exists() ? snap.data().count : 1)
    }).catch(() => {})
  }, [])

  // Load archive once
  useEffect(() => {
    getDocs(fsQuery(collection(db, 'archive'), orderBy('year', 'desc')))
      .then(snap => setArchiveItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
  }, [])

  // Filter related sermons
  useEffect(() => {
    if (!book || !chapter) { setRelatedSermons([]); return }
    setRelatedSermons(archiveItems.filter(item => archiveMatchesChapter(item.bible, book.id, chapter)))
  }, [book, chapter, archiveItems])

  // Font size
  const changeFont = (delta) => {
    setFontSize(prev => {
      const next = Math.min(24, Math.max(12, prev + delta))
      localStorage.setItem('bibleFontSize', String(next))
      return next
    })
  }

  // Fetch chapter
  const fetchChapter = useCallback(async (b, ch) => {
    setLoading(true)
    setError(null)
    setVerses([])
    setVersesAlt([])
    try {
      const primary = await fetchVerses(b, ch, lang)
      setVerses(primary)

      if (parallelRef.current) {
        const altLang = lang === 'ua' ? 'en' : 'ua'
        const alt = await fetchVerses(b, ch, altLang)
        setVersesAlt(alt)
      }

      setBook(b)
      setChapter(ch)
      setView('chapter')

      // Save last reading position
      localStorage.setItem('bibleLastPos', JSON.stringify({ bookId: b.id, chapter: ch }))
    } catch {
      setError(lang === 'en' ? 'Failed to load. Try again.' : 'Помилка завантаження. Спробуйте ще.')
    } finally {
      setLoading(false)
    }
  }, [lang])

  // Auto-restore last reading position on mount
  useEffect(() => {
    try {
      const lastPos = JSON.parse(localStorage.getItem('bibleLastPos') || 'null')
      if (!lastPos) return
      const b = BIBLE_BOOKS.find(bk => bk.id === lastPos.bookId)
      if (b) {
        if (lastPos.verse) setHighlight(String(lastPos.verse))
        fetchChapter(b, lastPos.chapter).then(() => {
          if (lastPos.verse) {
            setTimeout(() => {
              const el = document.getElementById(`verse-${lastPos.verse}`)
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }, 400)
          }
        })
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch when parallel toggled while in chapter view
  const bookRef = useRef(book)
  const chapterRef = useRef(chapter)
  const viewRef = useRef(view)
  useEffect(() => { bookRef.current = book }, [book])
  useEffect(() => { chapterRef.current = chapter }, [chapter])
  useEffect(() => { viewRef.current = view }, [view])

  useEffect(() => {
    if (viewRef.current === 'chapter' && bookRef.current) {
      fetchChapter(bookRef.current, chapterRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parallel])

  // Copy verse
  const copyVerse = (v) => {
    const bookName = lang === 'ua' ? book.ua : book.en
    navigator.clipboard.writeText(`${bookName} ${chapter}:${v.verse} — ${v.text}`)
      .then(() => {
        setCopied(v.verse)
        setTimeout(() => setCopied(null), 1500)
      })
  }

  // Share verse
  const shareVerse = (v) => {
    const bookName = lang === 'ua' ? book.ua : book.en
    const text = `${bookName} ${chapter}:${v.verse} — ${v.text}`
    if (navigator.share) {
      navigator.share({ text }).catch(() => {})
    } else {
      navigator.clipboard.writeText(text)
        .then(() => { setCopied(v.verse); setTimeout(() => setCopied(null), 1500) })
    }
  }

  // Bookmarks
  const isBookmarked = (bookId, ch, verse) =>
    bookmarks.some(b => b.bookId === bookId && b.chapter === ch && b.verse === verse)

  const toggleBookmark = (v) => {
    const idx = bookmarks.findIndex(b => b.bookId === book.id && b.chapter === chapter && b.verse === v.verse)
    let next
    if (idx >= 0) {
      next = [...bookmarks.slice(0, idx), ...bookmarks.slice(idx + 1)]
    } else {
      const entry = {
        bookId: book.id,
        bookUa: book.ua,
        bookEn: book.en,
        chapter,
        verse: v.verse,
        text: v.text,
        date: new Date().toISOString(),
      }
      next = [entry, ...bookmarks].slice(0, 100)
    }
    setBookmarks(next)
    saveBookmarks(next)
  }

  const removeBookmark = (bookId, ch, verse) => {
    const next = bookmarks.filter(b => !(b.bookId === bookId && b.chapter === ch && b.verse === verse))
    setBookmarks(next)
    saveBookmarks(next)
  }

  const openBookmark = (bm) => {
    const b = BIBLE_BOOKS.find(bk => bk.id === bm.bookId)
    if (!b) return
    setHighlight(String(bm.verse))
    fetchChapter(b, bm.chapter).then(() => {
      setTimeout(() => {
        const el = document.getElementById(`verse-${bm.verse}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 400)
    })
  }

  // Search
  const handleSearch = useCallback(async (e) => {
    e.preventDefault()
    const q = searchQuery.trim()
    if (!q) return

    const parsed = parseReference(q)
    if (parsed) {
      setHighlight(parsed.verseStart || '')
      await fetchChapter(parsed.book, parsed.chapter)
      if (parsed.verseStart) {
        setTimeout(() => {
          const el = document.getElementById(`verse-${parsed.verseStart}`)
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 400)
      }
      return
    }

    setLoading(true)
    setError(null)
    setSearchResult(null)
    try {
      let resultVerses
      if (lang === 'ua') {
        const res = await fetch(`${BOLLS_API}/search/UBIO/?search=${encodeURIComponent(q)}`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        resultVerses = data.map(v => ({
          book_name: BIBLE_BOOKS.find(bk => bk.bookNum === v.book)?.[lang] || '',
          chapter: v.chapter,
          verse: v.verse,
          text: v.text.replace(/<\/?mark>/g, ''),
        }))
      } else {
        const res = await fetch(`${BIBLE_API}/${encodeURIComponent(q)}?translation=web`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        resultVerses = data.verses || []
      }
      if (resultVerses.length) {
        setSearchResult({ q, verses: resultVerses })
        setView('search')
      } else {
        setError(lang === 'en' ? 'No results found.' : 'Нічого не знайдено.')
      }
    } catch {
      setError(lang === 'en'
        ? 'Enter a verse reference (e.g. "John 3:16") or book name.'
        : 'Введіть посилання (напр. "Ів. 3:16") або назву книги.')
    } finally {
      setLoading(false)
    }
  }, [searchQuery, lang, fetchChapter])

  const goToc = () => {
    setView('toc'); setBook(null); setVerses([]); setVersesAlt([])
    setSearchResult(null); setError(null); setHighlight('')
  }

  // ── Add verse to daily list ───────────────────────────────────────────────

  const addToDayVerses = async (v) => {
    if (!book) return
    setDayVerseStatus(s => ({ ...s, [v.verse]: 'saving' }))
    try {
      const dup = await getDocs(fsQuery(collection(db, 'verses'), where('text', '==', v.text)))
      if (!dup.empty) {
        setDayVerseStatus(s => ({ ...s, [v.verse]: 'exists' }))
        setTimeout(() => setDayVerseStatus(s => { const n = { ...s }; delete n[v.verse]; return n }), 2500)
        return
      }
      const vAlt = versesAlt.find(va => va.verse === v.verse)
      const rawAbbr = Object.entries(UA_REF_MAP).find(([, id]) => id === book.id)?.[0] || ''
      const fmtAbbr = rawAbbr
        ? (() => { const m = rawAbbr.match(/^(\d+)(.+)/); return m ? `${m[1]} ${m[2][0].toUpperCase()}${m[2].slice(1)}.` : `${rawAbbr[0].toUpperCase()}${rawAbbr.slice(1)}.` })()
        : book.ua
      const reference = `${fmtAbbr} ${chapter}:${v.verse}`
      const referenceEn = `${EN_REF_MAP[book.id] || book.en} ${chapter}:${v.verse}`
      await addDoc(collection(db, 'verses'), {
        reference,
        referenceEn,
        text: v.text,
        textEn: vAlt?.text || '',
        addedAt: serverTimestamp(),
      })
      setDayVerseStatus(s => ({ ...s, [v.verse]: 'added' }))
      setTimeout(() => setDayVerseStatus(s => { const n = { ...s }; delete n[v.verse]; return n }), 2500)
    } catch {
      setDayVerseStatus(s => { const n = { ...s }; delete n[v.verse]; return n })
    }
  }

  // ── Verse row ─────────────────────────────────────────────────────────────

  const renderVerse = (v) => {
    const vAlt = versesAlt.find(va => va.verse === v.verse)
    const isSelected = selectedVerse === v.verse
    const isHighlit = String(v.verse) === highlight || isSelected
    const bookmarked = isBookmarked(book.id, chapter, v.verse)

    const toggleSelect = (e) => {
      e.stopPropagation()
      setSelectedVerse(isSelected ? null : v.verse)
    }

    return (
      <div key={v.verse} style={{ position: 'relative' }}>
        <div
          id={`verse-${v.verse}`}
          onClick={toggleSelect}
          className={`bv-verse-row${isHighlit ? ' bv-verse-row--highlight' : ''}`}
        >
          <span className='bv-verse-num'>{v.verse}</span>
          {parallel ? (
            <div className='bv-parallel-cols'>
              <span className='bv-verse-text' style={{ fontSize }}>{v.text}</span>
              <span className='bv-verse-text bv-verse-text-alt' style={{ fontSize }}>{vAlt?.text || '…'}</span>
            </div>
          ) : (
            <span className='bv-verse-text' style={{ fontSize }}>{v.text}</span>
          )}
        </div>

        {isSelected && (
          <div className='bv-verse-popup' onClick={e => e.stopPropagation()}>
            {/* copy */}
            <button
              className={`bv-popup-btn${copied === v.verse ? ' bv-popup-btn--done' : ''}`}
              onClick={() => copyVerse(v)}
              title={lang === 'en' ? 'Copy' : 'Копіювати'}
            >
              <span className='bv-popup-icon'>{copied === v.verse ? '✓' : '⎘'}</span>
              <span className='bv-popup-label'>{lang === 'en' ? 'Copy' : 'Копіювати'}</span>
            </button>
            {/* share */}
            <button
              className='bv-popup-btn'
              onClick={() => shareVerse(v)}
              title={lang === 'en' ? 'Share' : 'Поділитися'}
            >
              <span className='bv-popup-icon'>↗</span>
              <span className='bv-popup-label'>{lang === 'en' ? 'Share' : 'Поділитися'}</span>
            </button>
            {/* bookmark */}
            <button
              className={`bv-popup-btn${bookmarked ? ' bv-popup-btn--active' : ''}`}
              onClick={() => toggleBookmark(v)}
              title={bookmarked
                ? (lang === 'en' ? 'Remove bookmark' : 'Видалити закладку')
                : (lang === 'en' ? 'Add bookmark' : 'Додати закладку')}
            >
              <span className='bv-popup-icon'>{bookmarked ? '★' : '☆'}</span>
              <span className='bv-popup-label'>{bookmarked
                ? (lang === 'en' ? 'Saved' : 'Збережено')
                : (lang === 'en' ? 'Save' : 'Зберегти')}
              </span>
            </button>
            {/* add to day verses — admin only */}
            {isAdmin && (() => {
              const st = dayVerseStatus[v.verse]
              return (
                <button
                  className={`bv-popup-btn${st === 'added' ? ' bv-popup-btn--done' : st === 'exists' ? ' bv-popup-btn--active' : ''}`}
                  onClick={() => addToDayVerses(v)}
                  disabled={st === 'saving' || st === 'added'}
                  title={lang === 'en' ? 'Add to verse of the day list' : 'Додати у Вірші дня'}
                >
                  <span className='bv-popup-icon'>
                    {st === 'added' ? '✓' : st === 'exists' ? '!' : st === 'saving' ? '…' : '📌'}
                  </span>
                  <span className='bv-popup-label'>
                    {st === 'added'
                      ? (lang === 'en' ? 'Saved' : 'Додано')
                      : st === 'exists'
                        ? (lang === 'en' ? 'Already in list' : 'Вже є')
                        : st === 'saving'
                          ? '…'
                          : (lang === 'en' ? 'Day verse' : 'Вірш дня')}
                  </span>
                </button>
              )
            })()}
            {/* divider */}
            <div className='bv-popup-divider' />
            {/* close */}
            <button
              className='bv-popup-close'
              onClick={() => setSelectedVerse(null)}
              title={lang === 'en' ? 'Close' : 'Закрити'}
            >
              ✕
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const primaryLabel = lang === 'ua' ? '🇺🇦 Огієнко' : '🇬🇧 World English'
  const altLabel     = lang === 'ua' ? '🇬🇧 World English' : '🇺🇦 Огієнко'

  return (
    <div className='bv-wrap'>
      {/* Toolbar */}
      <div className='bv-toolbar bible-padded'>
        <form onSubmit={handleSearch} className='bv-search-form'>
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={lang === 'en'
              ? 'Search — e.g. "John 3:16" or "love"'
              : 'Пошук — напр. "Ів. 3:16" або "любов"'}
            className='bv-search-input'
          />
          <button type='submit' className='bv-search-btn' disabled={loading}>
            {loading ? '…' : '🔍'}
          </button>
        </form>

        <div className='bv-controls'>
          <button
            className={`bv-control-btn${parallel ? ' bv-control-btn--active' : ''}`}
            onClick={() => setParallel(p => { const next = !p; localStorage.setItem('bibleParallel', next ? '1' : '0'); return next })}
            title={lang === 'en' ? 'Parallel translation' : 'Паралельний переклад'}
          >
            ∥
          </button>

          <div className='bv-font-row'>
            <button className='bv-font-btn' onClick={() => changeFont(-2)}>A−</button>
            <span className='bv-font-val'>{fontSize}</span>
            <button className='bv-font-btn' onClick={() => changeFont(2)}>A+</button>
          </div>

          {/* Bookmarks button */}
          <button
            className={`bv-control-btn${view === 'bookmarks' ? ' bv-control-btn--active' : ''}`}
            onClick={() => {
              if (view === 'bookmarks') {
                if (prevView === 'chapter' && book) setView('chapter')
                else goToc()
              } else {
                setPrevView(view)
                setView('bookmarks')
              }
            }}
            title={lang === 'en' ? 'Bookmarks' : 'Закладки'}
          >
            ★
            {bookmarks.length > 0 && (
              <span className='bv-bm-badge'>{bookmarks.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Parallel column headers */}
      {parallel && view === 'chapter' && (
        <div className='bv-parallel-header bible-padded'>
          <span className='bv-parallel-lang'>{primaryLabel}</span>
          <span className='bv-parallel-lang'>{altLabel}</span>
        </div>
      )}

      {readerCount !== null && (
        <div className='bv-reader-count bible-padded'>
          👁 {lang === 'en' ? `${readerCount} reading today` : `Сьогодні читають: ${readerCount}`}
        </div>
      )}

      {error && <div className='bv-error-box bible-padded'>{error}</div>}

      {/* TOC */}
      {view === 'toc' && (
        <div className='bv-toc bible-padded'>
          <BookGrid books={OT_BOOKS} label={lang === 'en' ? '📜 Old Testament' : '📜 Старий Заповіт'} lang={lang} onBook={b => fetchChapter(b, 1)} />
          <BookGrid books={NT_BOOKS} label={lang === 'en' ? '✝ New Testament' : '✝ Новий Заповіт'} lang={lang} onBook={b => fetchChapter(b, 1)} />
        </div>
      )}

      {/* Chapter */}
      {view === 'chapter' && book && (
        <div>
          <div className='bible-padded'>
            <ChapterNav
              book={book}
              chapter={chapter}
              lang={lang}
              onChapter={n => { setHighlight(''); fetchChapter(book, n) }}
              onBack={goToc}
            />
          </div>
          {loading
            ? <div className='bv-loading'>{lang === 'en' ? 'Loading…' : 'Завантаження…'}</div>
            : <div className='bv-verse-list'>{verses.map(v => renderVerse(v))}</div>
          }

          {!loading && verses.length > 0 && (
            <div className='bv-chapter-footer bible-padded'>
              {chapter > 1 && (
                <button className='bv-nav-footer-btn' onClick={() => { setHighlight(''); fetchChapter(book, chapter - 1) }}>
                  ← {lang === 'en' ? 'Previous' : 'Попередня'}
                </button>
              )}
              {chapter < book.chapters && (
                <button className='bv-nav-footer-btn' style={{ marginLeft: 'auto' }} onClick={() => { setHighlight(''); fetchChapter(book, chapter + 1) }}>
                  {lang === 'en' ? 'Next' : 'Наступна'} →
                </button>
              )}
            </div>
          )}

          {/* Related sermons */}
          {relatedSermons.length > 0 && (
            <div className='bv-sermons-panel bible-padded'>
              <div className='bv-sermons-title'>
                📺 {lang === 'en' ? 'Sermons on this chapter' : 'Проповіді на цю главу'} ({relatedSermons.length})
              </div>
              <div className='bv-sermons-list'>
                {relatedSermons.map(item => {
                  const vid = (item.link || '').match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/)
                  return (
                    <div key={item.id} className='bv-sermon-card'>
                      {vid && <img src={`https://img.youtube.com/vi/${vid[1]}/mqdefault.jpg`} alt='' className='bv-sermon-thumb' />}
                      <div className='bv-sermon-info'>
                        <div className='bv-sermon-title'>{item.title}</div>
                        <div className='bv-sermon-meta'>{item.author} · {item.year}</div>
                        {item.bible && <div className='bv-sermon-bible'>{item.bible}</div>}
                      </div>
                      {vid && (
                        <a href={`https://www.youtube.com/watch?v=${vid[1]}`} target='_blank' rel='noopener noreferrer' className='bv-sermon-link'>▶</a>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bookmarks view */}
      {view === 'bookmarks' && (
        <div className='bible-padded'>
          <div className='bv-search-header'>
            <button className='bv-back-btn' onClick={() => {
              if (prevView === 'chapter' && book) setView('chapter')
              else goToc()
            }}>
              ← {prevView === 'chapter' && book
                ? (lang === 'en' ? book.en : book.ua)
                : (lang === 'en' ? 'Books' : 'Книги')}
            </button>
            <span className='bv-breadcrumb'>
              {lang === 'en' ? 'Bookmarks' : 'Закладки'}
              {bookmarks.length > 0 && <span className='bv-bm-count'> ({bookmarks.length})</span>}
            </span>
          </div>
          {bookmarks.length === 0 ? (
            <div className='bv-bm-empty'>
              <div className='bv-bm-empty-icon'>☆</div>
              <div>{lang === 'en'
                ? 'No bookmarks yet. Tap ☆ on any verse to save it.'
                : 'Закладок ще немає. Натисніть ☆ на будь-якому вірші, щоб зберегти.'}</div>
            </div>
          ) : (
            <div className='bv-bm-list'>
              {bookmarks.map((bm, i) => {
                const bookName = lang === 'ua' ? bm.bookUa : bm.bookEn
                return (
                  <div key={i} className='bv-bm-card' onClick={() => openBookmark(bm)}>
                    <div className='bv-bm-card-header'>
                      <span className='bv-bm-ref'>★ {bookName} {bm.chapter}:{bm.verse}</span>
                      <span className='bv-bm-date'>{formatRelativeDate(bm.date, lang)}</span>
                      <button
                        className='bv-bm-remove'
                        onClick={e => { e.stopPropagation(); removeBookmark(bm.bookId, bm.chapter, bm.verse) }}
                        title={lang === 'en' ? 'Remove' : 'Видалити'}
                      >
                        ×
                      </button>
                    </div>
                    <div className='bv-bm-text'>
                      {bm.text?.length > 140 ? bm.text.slice(0, 140) + '…' : bm.text}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Search results */}
      {view === 'search' && searchResult && (
        <div className='bible-padded'>
          <div className='bv-search-header'>
            <button className='bv-back-btn' onClick={goToc}>← {lang === 'en' ? 'Books' : 'Книги'}</button>
            <span className='bv-breadcrumb'>
              {lang === 'en' ? `Results for "${searchResult.q}"` : `Результати: "${searchResult.q}"`}
            </span>
          </div>
          <div className='bv-verse-list'>
            {searchResult.verses.map((v, i) => (
              <div key={i} className='bv-verse-row'>
                <span
                  className='bv-verse-num bv-ref-link'
                  onClick={() => {
                    const b = BIBLE_BOOKS.find(bk => bk.en === v.book_name || bk.ua === v.book_name)
                    if (!b) return
                    setHighlight(String(v.verse))
                    fetchChapter(b, v.chapter).then(() => {
                      setTimeout(() => {
                        const el = document.getElementById(`verse-${v.verse}`)
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                      }, 400)
                    })
                  }}
                >
                  {v.book_name} {v.chapter}:{v.verse}
                </span>
                <span className='bv-verse-text' style={{ fontSize }}>{v.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && view !== 'chapter' && (
        <div className='bv-loading'>{lang === 'en' ? 'Loading…' : 'Завантаження…'}</div>
      )}
    </div>
  )
}

export default BibleViewer
